import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProfileSection = () => {
  const { user, userProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (userProfile) {
      setFormData({
        fullName: userProfile?.full_name || '',
        email: userProfile?.email || '',
      });
    }
  }, [userProfile]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          full_name: formData?.fullName,
        })?.eq('id', user?.id);

      if (updateError) throw updateError;

      setSuccess('Profiel succesvol bijgewerkt');
      setEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: userProfile?.full_name || '',
      email: userProfile?.email || '',
    });
    setEditing(false);
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-semibold text-foreground">Profiel Informatie</h2>
          {!editing && (
            <Button variant="outline" iconName="Edit" onClick={() => setEditing(true)}>
              Bewerken
            </Button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-md">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-success/10 border border-success/20 rounded-md">
            <p className="text-sm text-success">{success}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="User" size={32} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-foreground">{userProfile?.full_name || 'Leverancier'}</h3>
              <p className="text-sm text-muted-foreground">{userProfile?.email}</p>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 mt-2">
                Leverancier Account
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Volledige Naam"
              value={formData?.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
              disabled={!editing}
            />
            <Input
              label="Email"
              value={formData?.email}
              disabled
              description="Email kan niet worden gewijzigd"
            />
          </div>
        </div>

        {editing && (
          <div className="flex items-center space-x-3 mt-6 pt-6 border-t border-border">
            <Button onClick={handleSave} loading={loading}>
              Opslaan
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={loading}>
              Annuleren
            </Button>
          </div>
        )}
      </div>
      {/* Account Stats */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Account Statistieken</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Lid sinds</p>
            <p className="text-lg font-semibold text-foreground">
              {userProfile?.created_at
                ? new Date(userProfile.created_at)?.toLocaleDateString('nl-NL', {
                    month: 'long',
                    year: 'numeric',
                  })
                : '-'}
            </p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Account Type</p>
            <p className="text-lg font-semibold text-foreground">Leverancier</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Status</p>
            <p className="text-lg font-semibold text-success">Actief</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;